/** Строки окна «Проверка допработ за период» (10.10.2026): человек дня — его игры, админство и
 *  все допработы (назначенные и заявки — каждая один раз) с суммой из зарплаты и тревогами
 *  двойной оплаты. Тап по строке раскрывает действия; правки идут теми же запросами, что
 *  у сводки дня (DayWork): PUT/DELETE /api/extra-work/:id, hall-admin-reports, finance/ha-pay. */
import { useEffect, useState, type ReactElement } from "react";
import { hrs, type RvAdmin, type RvCalc, type RvPerson, type RvWork } from "./extraReviewModel";
import css from "./extraReview.module.css";

const fmt = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
/** Длина интервала в минутах; конец раньше начала — через полночь (как в зарплате). */
const spanMin = (f?: string | null, t?: string | null): number => {
  const p = (s?: string | null) => { const m = /^(\d{1,2}):(\d{2})/.exec(s ?? ""); return m ? Number(m[1]) * 60 + Number(m[2]) : 0; };
  const a = p(f), b = p(t); return Math.max(0, (b < a ? b + 1440 : b) - a);
};
const STATE: Record<RvWork["state"], string> = {
  paid: "назначена", proposal: "заявка · ждёт решения", rework: "на доработке у оператора", rejected: "отклонена", study: "учёбка · без оплаты",
};

export interface RowActions {
  /** запрос + перезагрузка окна */
  call: (path: string, method: string, body?: Record<string, unknown>) => Promise<void>;
  money: (anchor: HTMLElement, label: string, value: number, save: (v: number) => Promise<void>) => void;
  time: (label: string, value: string, save: (from: string, to: string) => Promise<void>) => void;
}

/** «план 14:00–20:00 = 6 ч − игра 17:00 Фазм 1,5 ч = 4,5 ч × ₽150/ч = ₽675» — как считает зарплата (11.10, владелец). */
function formula(c: RvCalc, pay: number): string {
  const minus = c.games.map((g) => ` − игра ${g.label} ${hrs(g.min)}`).join("");
  /* игры перекрывают друг друга и съедают больше времени работы — зарплата обнуляет, так и пишем */
  const over = c.games.reduce((n, g) => n + g.min, 0) > c.raw;
  return `${c.basis} ${c.from}–${c.to} = ${hrs(c.raw)}${minus}${c.games.length ? ` = ${over ? "0 ч (игры перекрыли всё время)" : hrs(c.eff)}` : ""} × ₽${fmt(c.rate)}/ч = ₽${fmt(pay)}`;
}

export function PersonBlock({ p, date, a }: { p: RvPerson; date: string; a: RowActions }): ReactElement {
  return (
    <div className={`${css.person} ${p.warn ? css.personWarn : ""}`}>
      <div className={css.pHead}>
        <b className={css.pName}>{p.name}</b>
        <span className={css.pGames}>
          {p.games.length ? `игры: ${p.games.map((g) => `${g.time}${g.quest ? ` ${g.quest}` : ""}${g.cancelled ? " (отмена)" : ""}`).join(" · ")}` : "игр нет"}
        </span>
      </div>
      {p.admin ? <AdminRow h={p.admin} operatorId={p.operatorId} name={p.name} date={date} a={a} /> : null}
      {p.works.map((x) => <WorkRow key={x.w.id} x={x} a={a} />)}
    </div>
  );
}

function WorkRow({ x, a }: { x: RvWork; a: RowActions }): ReactElement {
  const [open, setOpen] = useState(false);
  const [arm, setArm] = useState(false);
  useEffect(() => { if (!arm) return; const t = setTimeout(() => setArm(false), 3000); return () => clearTimeout(t); }, [arm]);
  const w = x.w, path = `/api/extra-work/${w.id}`;
  const study = x.state === "study";
  const undecided = x.state === "proposal" || x.state === "rework" || x.state === "rejected";
  const fact = w.factFrom && w.factTo && (w.factFrom !== w.timeFrom || w.factTo !== w.timeTo)
    ? ` · по отчёту ${w.factFrom}–${w.factTo}${w.factOk ? "" : " (не подтверждено — платим по плану)"}` : "";
  const how = study ? "учёбка ученику не оплачивается"
    /* 11.10 (ревью): не в зарплате или отклонена — без формулы, иначе «6 ч × ₽150 = ₽0» вводит в заблуждение */
    : x.outOfSalary ? `по часам, ₽${fmt(w.hourlyRate)}/ч — в расчётку не входит` : x.state === "rejected" ? "отклонена — не платим"
    : !x.byHours ? `сумма вписана вручную: ₽${fmt(w.rate_override ?? 0)} — время игр её не уменьшает${x.calc ? ` · по часам было бы ${formula(x.calc, x.calc.byHoursPay)}` : ""}`
    : `${x.calc ? formula(x.calc, x.pay) : `по часам, ₽${fmt(w.hourlyRate)}/ч`} — ${x.state === "paid" ? "столько уйдёт в зарплату" : "столько заплатим, если принять"}`;
  /* автовычет игры (или игра внутри ручной суммы) — формула прямо в строке, без раскрытия */
  const showCalc = !!x.calc && x.calc.games.length > 0 && !study && !x.outOfSalary && x.state !== "rejected";
  return (
    <div className={`${css.work} ${css[`st_${x.state}`] ?? ""}`}>
      <button type="button" className={`${css.wMain} ${open ? css.wMainOpen : ""}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={css.wIco}>🔧</span>
        <span className={css.wTime}>{w.timeFrom}–{w.timeTo}</span>
        <span className={css.wDesc}>{w.description || "без описания"}</span>
        <span className={css.wState}>{STATE[x.state]}{w.draft ? " · не отправлено" : ""}</span>
        {study ? null : <span className={css.wPay} title={x.byHours ? "по часам" : "сумма вписана вручную"}>₽{fmt(x.pay)}{x.byHours ? "" : " ✎"}</span>}
      </button>
      {showCalc && x.calc ? (
        <div className={css.wCalc}>{x.byHours ? <>{x.state === "paid" ? "к оплате" : "если принять"}: {formula(x.calc, x.pay)}</>
          : <>вписано вручную <b>₽{fmt(w.rate_override ?? 0)}</b> · по часам было бы: {formula(x.calc, x.calc.byHoursPay)}</>}</div>
      ) : null}
      {x.note ? <div className={`${css.wNote} ${x.outOfSalary ? css.wOut : ""}`}>{x.note}</div> : null}
      {/* 10.10 (владелец: «отчёт сдан, а в расчётке нет»): что ждёт решения ОО — видно и решается сразу */}
      {x.state === "proposal" || x.state === "rework" ? (
        <div className={css.wQuick}>
          <span>{x.payable ? "не войдёт в расчётку, пока не примете" : "ДК/ОО — в расчётку не входит и после «Принять»"}</span>
          <button type="button" className={`${css.act} ${css.actOk}`} onClick={() => void a.call(path, "PUT", { status: "confirmed" })}>✓ Принять</button>
        </div>
      ) : null}
      {x.factWait && x.state !== "rejected" ? (
        <div className={css.wQuick}>
          <span>по отчёту {w.factFrom}–{w.factTo} ({hrs(spanMin(w.factFrom, w.factTo))}){x.state === "paid" && !x.outOfSalary ? ` — в расчётке по плану ${w.timeFrom}–${w.timeTo} (${hrs(spanMin(w.timeFrom, w.timeTo))}), пока не подтвердите` : ""}</span>
          <button type="button" className={`${css.act} ${css.actOk}`} onClick={() => void a.call(path, "PUT", { fact_ok: 1 })}>✓ Время {w.factFrom}–{w.factTo}</button>
          <button type="button" className={css.act} onClick={() => void a.call(path, "PUT", { fact_reset: true })}>✕ Оставить план {w.timeFrom}–{w.timeTo}</button>
        </div>
      ) : null}
      {x.warns.map((t) => <div key={t} className={css.wWarn}>⚠ {t}</div>)}
      {open ? (
        /* 11.10 (владелец: «кнопки занимают очень много места, убери воздух; другой киберпанк»): команды терминала
           в одну строку с переносом — решение, оплата, удалить справа; без подписей групп */
        <div className={css.wOpen}>
          <div className={css.wFull}>{w.description || "без описания"}{fact}{w.reportText ? ` · отчёт: ${w.reportText}` : ""}</div>
          <div className={css.wHow}>{how}</div>
          <div className={css.wKeys}>
              {undecided && !study ? <button type="button" className={`${css.act} ${css.actOk}`} onClick={() => void a.call(path, "PUT", { status: "confirmed" })}>✓ Принять</button> : null}
              {x.state === "proposal" || x.state === "paid" ? <button type="button" className={`${css.act} ${css.actRw}`} onClick={() => void a.call(path, "PUT", { status: "rework" })}>↩ Вернуть</button> : null}
              {x.state === "proposal" || x.state === "rework" ? <button type="button" className={`${css.act} ${css.actNo}`} onClick={() => void a.call(path, "PUT", { status: "rejected" })}>✕ Отклонить</button> : null}
          {!study ? (<>
              <button type="button" className={css.act}
                onClick={() => a.time(`Отработал — ${w.operatorName}`, `${w.timeFrom}–${w.timeTo}`, (f, t) => a.call(path, "PUT", { time_from: f, time_to: t }))}>⏱ Время</button>
              <button type="button" className={css.act}
                onClick={(e) => a.money(e.currentTarget, `Ставка ₽/ч · ${w.operatorName}`, w.hourlyRate, (v) => a.call(path, "PUT", { hourly_rate: v, rate_override: null }))}>₽/ч</button>
              <button type="button" className={css.act}
                onClick={(e) => a.money(e.currentTarget, `Итого за допработу · ${w.operatorName}`, x.outOfSalary ? (w.rate_override ?? 0) : x.pay, (v) => a.call(path, "PUT", { rate_override: v }))}>Σ Сумма</button>
              {!x.byHours ? <button type="button" className={css.act} onClick={() => void a.call(path, "PUT", { rate_override: null })}>По часам</button> : null}
          </>) : null}
            <button type="button" className={`${css.act} ${css.actDel} ${arm ? css.actArm : ""}`}
              onClick={() => { if (!arm) { setArm(true); return; } void a.call(path, "DELETE"); }}>{arm ? "Точно удалить?" : "Удалить"}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminRow({ h, operatorId, name, date, a }: { h: RvAdmin; operatorId: string; name: string; date: string; a: RowActions }): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${css.work} ${css.adm}`}>
      <button type="button" className={`${css.wMain} ${open ? css.wMainOpen : ""}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={css.wIco}>◆</span>
        <span className={css.wTime}>{h.from && h.to ? `${h.from}–${h.to}` : "время?"}</span>
        <span className={css.wDesc}>админ {h.branch}{h.reported ? "" : " · часы филиала, отчёта смены нет"}</span>
        <span className={css.wState}>{h.hours} ч × ₽{fmt(h.rate)}</span>
        <span className={css.wPay}>₽{fmt(h.earning)}</span>
      </button>
      {h.warn ? <div className={css.wWarn}>⚠ {h.warn}</div> : null}
      {open ? (
        <div className={`${css.wOpen} ${css.wOpenAdm}`}>
          <div className={css.wKeys}>
            <button type="button" className={css.act}
              onClick={() => a.time(`Админство — ${name}`, h.from && h.to ? `${h.from}–${h.to}` : "",
                (f, t) => a.call("/api/hall-admin-reports", "POST", { operator_id: operatorId, branch_id: h.branchId, date, time_from: f, time_to: t }))}>⏱ Время смены</button>
            <button type="button" className={css.act}
              onClick={(e) => a.money(e.currentTarget, `Итого за смену · ${name}`, h.earning,
                (v) => a.call("/api/finance/ha-pay", "PUT", { staff_id: operatorId, date, branch_id: h.branchId, amount: v }))}>Σ Сумма за смену</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
